import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { myAccountDetailsApi } from '../services/apiService';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch profile/account details (persisted long-term in TanStack Query for the whole app)
 */
export function useProfileDetails(options = {}) {
  return useQuery({
    queryKey: queryKeys.user.profile(),
    queryFn: async () => {
      const response = await myAccountDetailsApi();
      if (response?.data?.res === true) {
        return response.data.data;
      }
      return response?.data?.data ?? response?.data ?? null;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - fresh for the whole day
    gcTime: 24 * 60 * 60 * 1000,    // 24 hours - cached across app & persistent storage
    meta: { persist: true },        // Persisted in sync storage
    refetchOnWindowFocus: false,
    ...options,
  });
}

export const useAccountProfile = useProfileDetails;

/**
 * Imperative fetch helper for profile details
 */
export async function fetchProfileDetails(queryClient = null) {
  const queryFn = async () => {
    const response = await myAccountDetailsApi();
    if (response?.data?.res === true) {
      return response.data.data;
    }
    return response?.data?.data ?? response?.data ?? null;
  };

  if (queryClient) {
    return queryClient.fetchQuery({
      queryKey: queryKeys.user.profile(),
      queryFn,
      staleTime: 24 * 60 * 60 * 1000,
      gcTime: 24 * 60 * 60 * 1000,
      meta: { persist: true },
    });
  }

  return queryFn();
}

/**
 * Invalidate user profile / account data across TanStack cache
 */
export function useInvalidateUserProfile() {
  const queryClient = useQueryClient();
  return () => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.user.all,
    });
  };
}
